
/**
 * Data-driven UI application logic that pertains to constructing UI link element.
 *
 * @author: Irina Patrikeeva <irina.i.patrikeeva@nasa.gov>
 *
 * Created on 09/01/2017
 * Modified on 12/12/2018
 */

/**
 * Construct a top-level URL for records without it
 * @param item {object} - JavaScript object of the record title row
 * @returns {string}
 */
function getBaseUrlValue( item ) {
    var result = '',
        model,
        record,
        data,
        hits,
        hitsArray,
        recordData;

    if ( item ) {
        // Retrieve cached UI model

        if ( GlobalPageState && GlobalPageState.type ) {
            model = getDataModel( GlobalPageState.type, false );
        } else {
            model = getDataModel( item._type, false );
        }

        // Retrieve ES response
        if ( GlobalPageState && GlobalPageState.data ) {
            data = GlobalPageState.data[ "metadata" ];
            if ( data ) {
                hits = data.hits;
                if ( hits ) {
                    hitsArray = hits.hits;
                    if ( hitsArray && hitsArray.length > 0 ) {
                        recordData = hitsArray[ 0 ]; // if there are multiple records, take the first one

                        if ( recordData && recordData._id === item._id ) {
                            record = recordData;
                        }
                    }
                }
            }
        }

        if ( ! ( model || record ) ) {
            // could not get the model from the server
            console.log("Error: model for " + item.type + " could not be fetched from the DMR");
        } else {
            result = getUrlFromTopBaseModel( model, record );
        }
    }

    return result;
}

/**
 * Construct a URL value from the top-level "base_url" object
 * @param model {object} - JavaScript UI model
 * @param record {object} - ES record data
 * @returns {string}
 */
function getUrlFromTopBaseModel( model, record ) {

    var result = '',
        properties,
        fieldModel,
        recordCollection,
        item;
    if ( model && record ) {
        properties = model.properties;
        if ( properties && properties.length === 1 ) {

            fieldModel = new BackboneModel;
            fieldModel.set( "name", "url" );
            fieldModel.set( "label", "URL" );
            fieldModel.set( "type", "string" );

            recordCollection = getBackboneCollectionFromSource( record );
            item = constructTopLevelBaseUrl( fieldModel, properties[ 0 ], recordCollection);
        }
    }

    if ( item ) {
        result = item.value;
    }

    return result;
}

/**
 * Construct a URL value from the parent "base_url" object for a nested field
 * @param item {object} - JavaScript object of the link field
 * @param model {object} - parent (nested) field model
 * @returns {string}
 */
function getUrlFromParentBaseModel( item, model ) {
    var result,
        value,
        baseUrlArray,
        fieldModel,
        properties,
        propertiesCollection;


    if ( model && item ) {

        baseUrlArray = model.base_url;
        fieldModel = new BackboneModel;

        fieldModel.set("name", item.name );
        fieldModel.set("label", item.label );
        fieldModel.set("type", item.type );

        properties = model.properties;
        propertiesCollection = getBackboneCollection( properties );
        value = constructParentBaseUrlItem( baseUrlArray, fieldModel, propertiesCollection );

    }

    if ( hasKeys( value ) && ! ( value === null || value === undefined ) ) {
        result = value.value;
    }

    return result;
}

/**
 * Retrieve or construct a link item
 * @param fieldModel {BackboneModel} - Backbone model of the link field in the search or UI model
 * @param recordModel {null|BackboneModel} - Backbone or JavaScript model of the link field in the ES record
 * @param propertiesObj {object} - search UI model properties object
 * @param recordCollection {BackboneCollection} - Backbone collection of ES result record
 * @param isNested {boolean} - true if link object is inside another object
 * @returns {null|object}
 */
function getLinkItem( fieldModel, recordModel, propertiesObj, recordCollection, isNested ) {

    var item = null;

    // Note - a single field with an array of primitive values cannot have a "multiple" flag and an "is_link" flag.
    // That case will return an undefined item
    if ( fieldModel && fieldModel.get( "multiple" ) && fieldModel.get( "is_link" ) ) {
        return item;
    }

    // Link value in the ES record is a valid link
    if ( recordModel && isValidLink( recordModel.get( "value") ) ) {
        item = getPrimitiveItem( fieldModel, recordModel );
    }

    // If the field object is nested, look at its immediate parent to get the base_url
    else if ( isNested ) {
        item = getParentBaseUrl( fieldModel, propertiesObj, recordCollection );
    }

    // If parent base_url is not found, use the top-level base_url object to construct the URL
    if ( ! item ) {
        item = constructTopLevelBaseUrl( fieldModel, propertiesObj, recordCollection );
    }

    return item;
}

/**
 * Construct a top-level base URL
 * @param fieldModel {BackboneModel} - Backbone model of the link field in the search or UI model
 * @param propertiesObj {object} - search UI model properties object
 * @param recordCollection {BackboneCollection} - Backbone collection of ES result record
 * @returns {*}
 */
function constructTopLevelBaseUrl( fieldModel, propertiesObj, recordCollection ) {
    var item,
        baseUrlArray,
        baseUrl,
        topUrlFound = false,
        j;

    if ( propertiesObj ) {

        baseUrlArray = propertiesObj.base_url;

        // Iterate through each item in the baseUrl array
        // Once a valid URL is constructed, return it
        if ( baseUrlArray && typeof baseUrlArray === "object" ) {
            for ( j = 0; j < baseUrlArray.length; j++ ) {
                baseUrl = baseUrlArray[ j ];
                if ( baseUrl && typeof baseUrl === "string" ) {
                    item = getBaseUrl( baseUrl, fieldModel, recordCollection );
                    if ( hasKeys( item ) && ! ( item.value === null || item.value === undefined ) ) {
                        topUrlFound = true;
                    }
                }
                if ( topUrlFound ) {
                    break;
                }
            }
        }
    }

    if ( ! topUrlFound ) {
        // If top base_url construction fails, look at the search_url field
        // The success callback will parse and display the URL if the record is found
        getSearchUrl( fieldModel, propertiesObj, recordCollection );
    }

    return item;
}

/**
 * Construct a base URL from a single value of the base_url array of values
 * Example
 * base_url: ["https://example.url.net?=/:DWGNO:(^=2)/:FILENAME:"]
 * The value of the field with the name "DWGO" will be replaced by the first 2 characters
 * If the character delimiter (^=N) is omitted, the parameter will be replaced by the whole value
 * If DWGNO is "12345" and FILENAME is "aaaaa", the output URL is "https://example.url.net?=/12/aaaaa"
 * @param baseUrl {string} - one value from the base_url array
 * @param fieldModel {BackboneModel} - Backbone model of the link field in the search or UI model
 * @param recordCollection {BackboneCollection} - Backbone collection of ES result record
 * @returns {*}
 */
function getBaseUrl( baseUrl, fieldModel, recordCollection ) {

    var item,
        regex,
        baseUrlReplaced,
        paramsArray,
        valueArray,
        valueModel,
        i;
    regex = getBaseUrlRegex();

    if ( baseUrl ) {

        baseUrlReplaced = baseUrl.replace( regex, "$1" );

        // Get parameter names from baseUrl
        paramsArray = getBaseUrlParameters( baseUrl );

        if ( paramsArray ) {

            // Get parameter value from recordCollection
            valueArray = getBaseUrlValues( paramsArray, recordCollection );

            if ( paramsArray.length === valueArray.length ) {

                // Replace parameter names with parameter values in the input string
                for ( i = 0; i < paramsArray.length; i++ ) {
                    var param = paramsArray[i];
                    var value = valueArray[i];
                    baseUrlReplaced = baseUrlReplaced.replace( param, value );
                }

                baseUrlReplaced = replaceValueCharacters( baseUrlReplaced, valueArray );

                if ( baseUrlReplaced ) {
                    valueModel = new BackboneModel();
                    valueModel.set( "value", baseUrlReplaced );
                    item = getPrimitiveItem( fieldModel, valueModel );

                }
            }
        }
    }

    return item;
}

/**
 * Take the first N characters if an URL parameter has a character modifier after it
 * In the form /:parameter:(^=N) where N is a number between 1 and 9
 * If the input URL doesn't have the character modifier, return the original string
 * @param baseUrl {string} - one value from the base_url array with parameters replaced by values
 * @param valueArray {string} - array of URL parameter values
 * @returns {string}
 */
function replaceValueCharacters( baseUrl, valueArray ) {

    var characterModifier = '(^=',
        indexes = [],
        pos,
        start,
        val,
        valueUnreplaced,
        replacingValue,
        valueToReplace,
        index,
        nChars,
        i;

    // check to see if there are characters that need to be extracted from the whole parameter value
    // check if character notation is next to the parameter value

    if ( baseUrl && baseUrl.indexOf( characterModifier ) > -1 ) {
        // find all occurrences of the character modifier string in the URL
        pos = 0;
        start = -1;
        while (pos !== -1) {
            pos = baseUrl.indexOf( characterModifier, start + 1 );
            if ( pos !== -1 ) {
                indexes.push( pos );
            }
            start = pos;
        }

        // Replace parameter names with parameter values in the input string
        for ( i = 0; i < valueArray.length; i++ ) {
            val = valueArray[ i ];
            // find where value plus optional character modifier occurs
            if ( val ) {
                valueUnreplaced = val + characterModifier;
                if ( baseUrl.indexOf( valueUnreplaced ) > -1 && characterModifier ) {
                    // check that the number of values to replace is less than or equal the number of parameters
                    if ( indexes.length > 0 && i <= indexes.length - 1 ) {
                        // assume the chars to take is between 1 and 9
                        index = indexes[i] + characterModifier.length;
                        nChars = baseUrl.charAt( index );
                        if ( nChars && Number(nChars) < 10 && Number(nChars) > 0 ) {
                            replacingValue = valueUnreplaced.substr( 0, Number( nChars ));
                            // Check that the number of characters to keep is less than or equal to the length of the value to shorten
                            if ( replacingValue && Number( nChars ) <= val.length ) {
                                valueToReplace = valueUnreplaced + nChars + ")";
                                baseUrl = baseUrl.replace( valueToReplace, replacingValue );
                            }
                        }
                    }
                }
            }
        }
    }

    return baseUrl;
}

/**
 * Construct a base URL from a "search_url" object
 * @param fieldModel {BackboneModel} - Backbone model of the link field in the search or UI model
 * @param propertiesObj {object} - search UI model properties object
 * @param recordCollection {BackboneCollection} - Backbone collection of ES result record
 */
function getSearchUrl( fieldModel, propertiesObj, recordCollection ) {

    var item,
        searchUrl,
        url;

    if ( propertiesObj ) {
        searchUrl = propertiesObj.search_url;

        // get a search_url object value
        // construct the url to call
        if ( searchUrl && typeof searchUrl === "string" ) {
            item = getBaseUrl( searchUrl, fieldModel, recordCollection );

            if ( item && ! ( item.value === null || item.value === undefined ) ) {

                url = item.value;
                if ( isValidLink( url )) {
                    // make an ajax call
                    makeAjaxRequest( url, searchUrlDataFound( propertiesObj ), dataNotFound( url ) );
                }
            }
        }
    }
}

/**
 * Return a function that parses the record fetched by the search_url
 * @returns {Function}
 */
function searchUrlDataFound( propertiesObj ) {

    /**
     * Callback that is called when the call to fetch the model a search_url succeeds
     * @param data {object} - data model JSON object
     * @param textStatus {string} - status of the request
     * @param jqXHR {object} - jQuery XMMLHTTPRequest object
     */
    return function( data, textStatus, jqXHR ) {

        var record;
        if ( data && data.hits && data.hits.hits ) {
            // check if a single record is found
            if ( data.hits.hits.length === 1 ) {
                record = data.hits.hits[ 0 ];

                displaySearchUrl( record, propertiesObj );

            }
        }
    }
}

/**
 * Display the updated search url in the header region
 * @param record {object} - ES data record
 * @param propertiesObj {object} - UI model properties object of the current record
 */
function displaySearchUrl( record, propertiesObj ) {
    var value,
        headerRegion,
        headerView,
        headerModel,
        jsonModel = {};

    // construct a model object expected by the getUrlFromTopBaseModel method
    jsonModel.properties = [ propertiesObj ];

    value = getUrlFromTopBaseModel( jsonModel, record );

    if ( value && isValidLink( value ) ) {

        if ( GlobalPageState && GlobalPageState.layout ) {
            headerRegion = GlobalPageState.layout.getRegion("headerRegion");
            if ( headerRegion ) {
                // re-display the header region with the url value
                headerView = headerRegion.currentView;

                if ( headerView ) {
                    headerModel = headerView.model;
                    headerModel.set("url", value);
                    headerView.render();
                }
            }
        }
    }
}

/**
 * Get a link object with value constructed from the base_url object in its immediate parent
 * @param fieldModel {BackboneModel} - Backbone model of the link field in the search or UI model
 * @param propertiesObj {object} - search UI model properties object
 * @param recordCollection {BackboneCollection} - Backbone collection of search result record
 * @returns {Object}
 */
function getParentBaseUrl( fieldModel, propertiesObj, recordCollection ) {

    var fieldName,
        propertiesCollection,
        parentModel,
        baseUrlArray,
        item;

    if ( fieldModel ) {

        fieldName = fieldModel.get( "name" );

        if ( fieldName && propertiesObj ) {

            propertiesCollection = getBackboneCollection( propertiesObj.properties );
            parentModel = findFieldBaseUrlObject( propertiesCollection, fieldName, null );

            if ( parentModel ) {

                baseUrlArray = parentModel.get( "base_url" ); // array of strings
                item = constructParentBaseUrlItem( baseUrlArray, fieldModel, recordCollection );
            }
        }
    }

    return item;
}

/**
 *
 * @param baseUrlArray {object} - "base_url" value object (array of strings)
 * @param fieldModel {BackboneModel} - Backbone model of the link field in the search or UI model
 * @param recordCollection {BackboneCollection} - Backbone collection of search result record
 * @returns {*}
 */
function constructParentBaseUrlItem( baseUrlArray, fieldModel, recordCollection ) {

    var baseUrl,
        regex,
        baseUrlReplaced,
        paramsArray,
        valueArray,
        valueModel,
        item,
        i,
        j;

    // Iterate through each item in the baseUrl array
    // Once a valid URL is constructed, return it
    if ( baseUrlArray && typeof baseUrlArray === "object" ) {
        for ( j = 0; j < baseUrlArray.length; j++ ) {
            baseUrl = baseUrlArray[ j ];
            if ( baseUrl && typeof baseUrl === "string" ) {

                regex = getBaseUrlRegex();
                baseUrlReplaced = baseUrl.replace( regex, "$1" );

                paramsArray = getBaseUrlParameters( baseUrl );
                if ( paramsArray ) {
                    // Get parameter value from recordCollection
                    valueArray = getBaseUrlValues( paramsArray, recordCollection );

                    if ( paramsArray.length === valueArray.length ) {

                        // Replace parameter names with parameter values in the input string
                        for ( i = 0; i < paramsArray.length; i++ ) {
                            baseUrlReplaced = baseUrlReplaced.replace( paramsArray[i], valueArray[i] );
                        }

                        if ( baseUrlReplaced ) {
                            valueModel = new BackboneModel();
                            valueModel.set( "value", baseUrlReplaced );
                            item = getPrimitiveItem( fieldModel, valueModel );
                        }
                    }
                }
            }

            if ( hasKeys( item ) && ! ( item.value === null || item.value === undefined ) ) {
                break;
            }
        }
    }

    return item;
}

/**
 * Get the object that has a nonempty "base_url" object by the name of one of its immediate child
 * @param collection {BackboneCollection} - collection of models
 * @param name {string} - field name that could have an immediate parent with a "base_url" key
 * @param parentModel {BackboneModel} - Backbone model of the parent (if it exists)
 * @returns {*}
 */
function findFieldBaseUrlObject( collection, name, parentModel ) {

    var model,
        properties,
        baseUrl,
        propertiesCollection,
        i,
        j;

    if ( collection && collection.models ) {
        for ( i = 0; i < collection.models.length; i++ ) {
            model = collection.models[i];

            if ( model.get("type") === "object") {
                // Look at the array of properties
                properties = model.get("properties");
                baseUrl = model.get("base_url");
                if ( properties ) {

                    if ( baseUrl ) {
                        // check that the name is in the properties array
                        for ( j = 0; j < properties.length; j++ ) {
                            if ( properties[j] && properties[ j ].name === name ) {
                                return model;
                            }
                        }
                    }
                    else {
                        propertiesCollection = getBackboneCollection( properties );
                        return findFieldBaseUrlObject( propertiesCollection, name, parentModel );
                    }
                }

            }
        }
    }
    return parentModel;
}


/**
 * Get a regular expression to find words surrounded by colons
 * Example:  /some-url/:parameter-one:/other/:parameter-two:
 * @returns {RegExp}
 */
function getBaseUrlRegex() {
    var regex = /:([\w\-]+):/g;
    return regex;
}

/**
 * Get an array of parameters from base url that need to be replaced
 * @param url {string} - base url value
 * @returns {Array}
 */
function getBaseUrlParameters( url ) {

    var regex,
        paramsArray = [],
        param;

    if ( url && typeof url === "string" ) {
        regex = getBaseUrlRegex();
        if ( regex ) {
            paramsArray = [];
            while ( param = regex.exec( url ) ) {
                paramsArray.push( param[ 1 ] );
            }
        }
    }

    return paramsArray;
}


/**
 * Get an array of parameter values for base url fields
 * @param paramsArray {Array} - array of parameter names
 * @param recordCollection {BackboneCollection} - Backbone collection of search result record
 * @returns {Array}
 */
function getBaseUrlValues( paramsArray, recordCollection ) {

    var valueArray = [],
        model,
        value,
        nestedValue,
        i;

    if ( paramsArray ) {
        for ( i = 0; i < paramsArray.length; i++ ) {
            model =  lookupNestedFieldModel( recordCollection, paramsArray[i] );
            if ( model ) {
                value = model.get( "value" );
                if ( value ) {
                    if ( typeof value === "object") {
                        if ( value && value.length > 0 ) {
                            // For arrays of values -- right now only the first item becomes a link
                            nestedValue = value[ 0 ][ paramsArray[ i ] ];
                            if ( nestedValue ) {
                                valueArray.push( nestedValue );
                            }
                        }
                    } else {
                        // primitive value
                        valueArray.push( value );
                    }
                }
            }
        }
    }

    return valueArray;
}


/**
 * Update link items with values constructed from "base_url" object
 * @param itemsArray {[object]} - array of the final item objects
 * @param record {object} - ES data record
 * @param model {object} - nested field model
 * @param jsonModel {object} - JavaScript UI model
 * @returns {*}
 */
function updateLinkItems( itemsArray, record, model, jsonModel ) {

    var itemArray,
        item,
        value,
        i,
        j;

    if ( itemsArray ) {

        for ( j = 0; j < itemsArray.length; j++ ) {
            itemArray = itemsArray[j];

            if (itemArray) {
                for ( i = 0; i < itemArray.length; i++ ) {
                    item = itemArray[ i ];
                    // if there are links with no value, construct the value from the base_url attribute of the parent
                    if ( item && item.is_link && ! item.value && model ) {

                        // check model's base_url object
                        value = getUrlFromParentBaseModel( item, model );

                        // if not found, check top-level base_url object
                        if ( ! value ) {
                            value = getUrlFromTopBaseModel( jsonModel, record );
                        }

                        // If value constructed, update link item with new value
                        item.value = value;
                    }
                }
            }
        }
    }

    return itemsArray;
}


/**
 * Construct a top-level edit URL for records without it
 * @param item {object} - JavaScript object of the record title row
 * @returns {string}
 */
function getEditUrlValue( item ) {
    var result = '',
        model,
        record,
        data,
        hits,
        hitsArray,
        recordData;

    if ( item ) {
        // Retrieve cached UI model
        if ( GlobalPageState && GlobalPageState.type ) {
            model = getDataModel( GlobalPageState.type, false );
        } else {
            model = getDataModel( item._type, false );
        }

        // Retrieve ES response
        if ( GlobalPageState && GlobalPageState.data ) {
            data = GlobalPageState.data[ "metadata" ];
            if ( data ) {
                hits = data.hits;
                if ( hits ) {
                    hitsArray = hits.hits;
                    if ( hitsArray && hitsArray.length === 1 ) {
                        recordData = hitsArray[ 0 ];

                        if ( recordData && recordData._id === item._id ) {
                            record = recordData;
                        }
                    }
                }
            }
        }

        if ( ! ( model || record ) ) {
            // could not get the model from the server
            console.log("Error: model for " + item.type + " could not be fetched from the DMR");
        } else {
            result = getEditUrlFromTopBaseModel( model, record );
        }
    }

    return result;
}

/**
 * Construct an edit URL value from the top-level "edit_url" object
 * @param model {object} - JavaScript UI model
 * @param record {object} - ES record data
 * @returns {string}
 */
function getEditUrlFromTopBaseModel( model, record ) {

    var result = '',
        properties,
        fieldModel,
        recordCollection,
        item;
    if ( model && record ) {
        properties = model.properties;
        if ( properties && properties.length === 1 ) {

            fieldModel = new BackboneModel;
            fieldModel.set( "name", "url" );
            fieldModel.set( "label", "URL" );
            fieldModel.set( "type", "string" );

            recordCollection = getBackboneCollectionFromSource( record );
            item = constructTopLevelEditUrl( fieldModel, properties[ 0 ], recordCollection);
        }
    }

    if ( item ) {
        result = item.value;
    }

    return result;
}

/**
 * Construct a top-level edit URL
 * @param fieldModel {BackboneModel} - Backbone model of the link field in the search or UI model
 * @param propertiesObj {object} - search UI model properties object
 * @param recordCollection {BackboneCollection} - Backbone collection of ES result record
 * @returns {*}
 */
function constructTopLevelEditUrl( fieldModel, propertiesObj, recordCollection ) {
    var item,
        editUrlArray,
        editUrl,
        topUrlFound = false,
        j;

    if ( propertiesObj ) {

        editUrlArray = propertiesObj.edit_url;

        // Iterate through each item in the baseUrl array
        // Once a valid URL is constructed, return it
        if ( editUrlArray && typeof editUrlArray === "object" ) {
            for ( j = 0; j < editUrlArray.length; j++ ) {
                editUrl = editUrlArray[ j ];
                if ( editUrl && typeof editUrl === "string" ) {
                    item = getBaseUrl( editUrl, fieldModel, recordCollection );
                    if ( hasKeys( item ) && ! ( item.value === null || item.value === undefined ) ) {
                        topUrlFound = true;
                    }
                }
                if ( topUrlFound ) {
                    break;
                }
            }
        }
    }

    return item;
}
